import { TFunction } from 'i18next';
import {
  PapiUser,
  PapiDacheckoutPurchase,
  PapiRequestDacheckoutPurchase,
  PapiOrderV3,
  PapiProductV3,
} from '@wix/da-papi-types';
import { formatDollars, TWO_DECIMAL_DIGITS } from '@wix/da-shared-react/pkg/utils/formatting';
import { dollarsToPoints, pointsToDollars } from '@wix/da-shared-react/pkg/utils/points';
import { requestNapiAsync } from '@wix/da-http-client/pkg/async';
import { PaymentMethod, CreditCardType } from '../types';
import { BiData, PaymentButtonClickBiEvent } from '@wix/da-bi/pkg/events';
import { API_BASE_PATH } from '../../constants';

/**
 * Select the appropriate price value based on payment method.
 * Centralizes the `method === POINTS ? x.points : x.usd` pattern.
 */
export const selectPriceByMethod = (
  method: PaymentMethod,
  values: { points: number; usd: number }
): number => (method === PaymentMethod.POINTS ? values.points : values.usd);

// Tip price structure with USD and Points values
export interface TipPrice {
  usd: number;
  points: number;
}

// Build a TipPrice from either USD or Points as the source value
export const buildTipPrice = (amount: number, isPointsSource: boolean): TipPrice => {
  if (isPointsSource) {
    const usd = parseFloat(pointsToDollars(amount));
    return { usd, points: amount };
  }
  return { usd: amount, points: dollarsToPoints(amount) };
};

// Convert tip price - sourceMethod indicates which value (usd or points) is authoritative
export const convertTipPrice = (price: TipPrice, sourceMethod: PaymentMethod): TipPrice => {
  return buildTipPrice(
    sourceMethod === PaymentMethod.POINTS ? price.points : price.usd,
    sourceMethod === PaymentMethod.POINTS
  );
};

// Tip validation limits
export const TIP_LIMITS = {
  USD: { min: 1, max: 200 },
  POINTS: { min: 1, max: 16000 },
} as const;

export interface TipValidationResult {
  valid: boolean;
  errorKey?: string;
}

// Validate tip amount based on payment method
export const validateTipAmount = (amount: number, isPoints: boolean): TipValidationResult => {
  if (isNaN(amount)) {
    return { valid: false, errorKey: 'checkout.tip.error.invalidAmount' };
  }

  if (isPoints) {
    if (!Number.isInteger(amount)) {
      return { valid: false, errorKey: 'checkout.tip.error.wholeNumbersOnly' };
    }
    if (amount < TIP_LIMITS.POINTS.min) {
      return { valid: false, errorKey: 'checkout.tip.error.minAmountPoints' };
    }
    if (amount > TIP_LIMITS.POINTS.max) {
      return { valid: false, errorKey: 'checkout.tip.error.maxAmountPoints' };
    }
  } else {
    if (amount < TIP_LIMITS.USD.min) {
      return { valid: false, errorKey: 'checkout.tip.error.minAmount' };
    }
    if (amount > TIP_LIMITS.USD.max) {
      return { valid: false, errorKey: 'checkout.tip.error.maxAmount' };
    }
  }

  return { valid: true };
};

// Tip structure for PAPI payment_info
export interface TipPaymentInfo {
  price: {
    usd: number;
    points: number;
  };
}

// Parse tip data from form submission (used by server)
export const parseTipFromFormData = (
  tipUsd?: string,
  tipPoints?: string
): TipPaymentInfo | undefined => {
  if (tipUsd === undefined || tipUsd === '') {
    return undefined;
  }

  const usd = parseFloat(tipUsd);
  const points = parseInt(tipPoints || '0', 10);

  // Validate parsed values
  if (isNaN(usd) || usd <= 0) {
    return undefined;
  }

  return {
    price: {
      usd,
      points: isNaN(points) ? 0 : points,
    },
  };
};

export const getOrderV3TotalPrice = (method: PaymentMethod, order: PapiOrderV3) => {
  const total = order.product.total;
  if (!total) return undefined;
  return selectPriceByMethod(method, total);
};

export const getOrderV3ProductPriceBeforeDiscount = (method: PaymentMethod, order: PapiOrderV3) => {
  const priceBeforeDiscount = order.product.discount?.priceBeforeDiscount;
  if (priceBeforeDiscount) {
    return selectPriceByMethod(method, priceBeforeDiscount) || 0;
  }
  return selectPriceByMethod(method, order.product.price) || 0;
};

export const getOrderV3ProductPriceBeforeDiscountInUSD = (order: PapiOrderV3) => {
  return order.product.discount?.priceBeforeDiscount?.usd || order.product.price.usd || 0;
};

export const getOrderDiscountV3 = (method: PaymentMethod, order: PapiOrderV3) => {
  const discount = order.product.discount;
  if (!discount) return 0;
  return selectPriceByMethod(method, discount.amount) || 0;
};

export const getDiscountCampaignLabel = (order: PapiOrderV3) =>
  order.product.discount?.campaignLabel;

export const getDiscountPercent = (order: PapiOrderV3) =>
  order.product.discount?.amount.percent || 0;

export const getOrderHasDiscount = (order: PapiOrderV3) => !!order.product.discount?.amount.usd;

export const formatPrice = (
  t: TFunction,
  method: PaymentMethod,
  price: number,
  forceDecimals = false
) => {
  return method === PaymentMethod.POINTS
    ? t('price.points', {
        price: Number(price).toLocaleString(),
      })
    : t('price.usd', {
        price: formatDollars(price, forceDecimals ? TWO_DECIMAL_DIGITS : {}),
      });
};

// Code ported from green site
export const getCreditCardType = (number: string): CreditCardType | undefined => {
  if (!number || !number.match(/^\d+$/)) {
    return undefined;
  }
  if (number[0] === '4' && (number.length === 13 || number.length === 16)) {
    return CreditCardType.VISA;
  }
  const prefix2 = number.substr(0, 2);
  if ((prefix2 === '34' || prefix2 === '37') && number.length === 15) {
    return CreditCardType.AMEX;
  }
  const prefix2int = parseInt(prefix2, 10);
  if (
    ((prefix2int >= 51 && prefix2int <= 55) || (prefix2int >= 22 && prefix2int <= 27)) &&
    number.length === 16
  ) {
    return CreditCardType.MASTERCARD;
  }
  if (number.substr(0, 4) === '6011' && number.length === 16) {
    return CreditCardType.DISCOVER;
  }
};

export const isValidCreditCardNumber = (number: string) => {
  number = sanitizeCreditCardNumber(number);
  if (!getCreditCardType(number)) {
    return false;
  }
  // Luhn check, code ported from green site
  let sum = 0;
  for (let i = number.length - 1; i >= 0; i -= 2) {
    sum += parseInt(number[i], 10);
  }
  for (let i = number.length - 2; i >= 0; i -= 2) {
    let doubled = parseInt(number[i], 10) * 2;
    if (doubled >= 10) {
      doubled -= 9;
    }
    sum += doubled;
  }
  return sum % 10 === 0;
};
export const sanitizeCreditCardNumber = (number: string) => {
  return number.replace(/[^0-9]/g, '');
};

export const getOrderedProductTypeV3 = (order: PapiOrderV3): PapiProductV3['productType'] =>
  order.product.productType;

/**
 * Get the tip supplementary product from order if present
 */
export const getOrderTipProduct = (order: PapiOrderV3) => {
  return order.product.supplementaryProducts?.find(p => p.productType === 'tip');
};

/**
 * Get non-tip supplementary products (extras like originals_answer_addon)
 */
export const getOrderExtrasProducts = (order: PapiOrderV3) => {
  return order.product.supplementaryProducts?.filter(p => p.productType !== 'tip') || [];
};

/**
 * Get total price of extras (non-tip supplementary products)
 */
export const getOrderExtrasTotal = (method: PaymentMethod, order: PapiOrderV3): number => {
  const extras = getOrderExtrasProducts(order);
  return extras.reduce((sum, extra) => sum + selectPriceByMethod(method, extra.price), 0);
};

/**
 * Calculate the checkout total: order total + user-selected tip
 * Backend's order.product.total already includes extras/add-ons.
 * The only value not in total is the user-selected tip (entered on checkout).
 */
export const getCheckoutTotal = (
  method: PaymentMethod,
  order: PapiOrderV3,
  tipPrice: TipPrice | null | undefined
): number => {
  const orderTotal = getOrderV3TotalPrice(method, order);
  const tipValue = tipPrice ? selectPriceByMethod(method, tipPrice) : 0;
  return orderTotal + tipValue;
};

/**
 * Get the tip amount from order's supplementary products
 */
export const getOrderTipPrice = (method: PaymentMethod, order: PapiOrderV3): number => {
  const tipProduct = getOrderTipProduct(order);
  if (!tipProduct) return 0;
  return selectPriceByMethod(method, tipProduct.price);
};

/**
 * Get the total price from order (includes supplementary products like tips)
 * Falls back to product price if total is not available
 */
export const getOrderTotal = (method: PaymentMethod, order: PapiOrderV3): number => {
  const total = order.product.total;
  if (total) {
    return selectPriceByMethod(method, total);
  }
  // Fallback: calculate total from product price + tip
  return getOrderV3TotalPrice(method, order) + getOrderTipPrice(method, order);
};

const getFlowType = (isCorePurchase: boolean, hasCoreMembership: boolean, isGift: boolean) => {
  if (!isCorePurchase) {
    return undefined;
  }

  if (isGift) {
    return 'give_core';
  }

  if (hasCoreMembership) {
    return 'upgrade_core';
  }

  return 'become_core';
};

export const getPayBiDataV3 = (
  order: PapiOrderV3,
  paymentMethod: PaymentMethod,
  hasCoreMembership: boolean,
  giftUser?: PapiUser,
  /** Tip amount in USD */
  tipAmountUsd?: number
) => {
  const itemsIds = [order.product.subproductId];
  const flowType = getFlowType(
    getOrderedProductTypeV3(order) === 'core',
    hasCoreMembership,
    giftUser !== undefined
  );

  return BiData<PaymentButtonClickBiEvent>({
    evid: 520,
    payment_type: paymentMethod as any,
    item_total: order.product.total?.usd,
    num_items: itemsIds.length,
    subproductid: itemsIds.join(','),
    give_core_or_become_core: flowType as any,
    is_form_valid: 1,
    ...(tipAmountUsd !== undefined ? { tipamount: String(tipAmountUsd) } : {}),
  });
};

export function placeOrderAsync(
  data: PapiRequestDacheckoutPurchase,
  csrfToken: string
): Promise<PapiDacheckoutPurchase> {
  // /purchase is still proxied through node so we call NAPI here
  return requestNapiAsync(
    {
      method: 'post',
      baseURL: API_BASE_PATH,
      url: '/purchase',
      data,
    },
    { csrfToken }
  );
}
